import React, { useEffect, useState } from 'react';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Empty, Tooltip, Modal } from 'antd';
import EcommerceElementShopify from './EcommerceElementShopify';
import ShopifySettings from './ShopifySettings';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { useRouter } from 'next/router';
import {
  useFindManyUserSceneAssetsQuery,
  useGetShopifyStoreBySceneQuery,
} from '@src/components/generated/graphql';
import { SHOP_PRODUCTS_QUERY } from '../../side-bar-menu/shopify/queries';
import DefaultButton from '@src/components/ui-kit/buttons/DefaultButton';
import { ShopifyMetadata } from '@src/components/scene/react-three-components/sceneAsset';
import { useSession } from 'next-auth/react';
import { openNotification } from 'util/helpers';
import ecmrceStyles from '../../asset-store/created-assets/created-assets.module.css';
import styles from './shopify-products.module.css';

const notify = () => {
  openNotification(
    'Info',
    "If you can't see recently added assets there, please try to save scene.",
    'top',
    8
  );
};

const ShopifyProducts = ({ callback }) => {
  const [products, setProducts] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const [updateStore, setUpdateStore] = useState(false);

  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  const { data } = useGetShopifyStoreBySceneQuery({
    variables: {
      where: {
        sceneId: sceneId as string,
      },
    },
  });

  const store = data?.getShopifyStoreByScene.store;

  useEffect(() => {
    async function getData() {
      if (!store) {
        return;
      }
      const client = new ApolloClient({
        uri: `https://${store.domain}/api/${store.apiVersion}/graphql.json`,
        headers: {
          'X-Shopify-Storefront-Access-Token': store.token as string,
        },
        cache: new InMemoryCache(),
      });
      const { data } = await client.query({
        query: SHOP_PRODUCTS_QUERY,
      });
      setProducts(data.products.edges);
    }
    getData();
  }, [store]);

  const loadAsset = (name: string, url: string, id: string) => {
    if (!store) {
      return;
    }

    const metadata = {
      id,
      storeDomain: store.domain,
      storeToken: store.token,
      storeVersionAPI: store.apiVersion,
    } as ShopifyMetadata;

    callback(name, url, SIDE_MENU_EVENTS.IMAGE_PLANE, metadata);
  };

  const { data: sceneAssets } = useFindManyUserSceneAssetsQuery({
    variables: {
      where: {
        userId: {
          equals: session?.user.id,
        },
        userSceneId: {
          equals: sceneId,
        },
        shopifyMetadata: {
          not: null,
        },
        type: {
          equals: ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE, //Only images supported
        },
      },
    },
  });

  if (!store) {
    return <ShopifySettings sceneId={sceneId as string} />;
  }

  if (store && updateStore) {
    return (
      <ShopifySettings
        sceneId={sceneId as string}
        store={store as any}
        closeSettings={() => setUpdateStore(false)}
      />
    );
  }

  return (
    <div>
      <div /* className={styles.grid} */>
        <DefaultButton
          type='secondary'
          onClick={() => {
            setUpdateStore(true);
          }}
          style={{ marginBottom: 10 }}
        >
          <span>Update store config</span>
        </DefaultButton>
      </div>
      {products.length ? (
        <div className={styles.grid}>
          <div className={styles.ecmrce}>
            <DefaultButton
              type='secondary'
              onClick={() => {
                setOpenModal(true);
                notify();
              }}
              style={{ marginBottom: 5 }}
            >
              <span>Enable e-commerce</span>
            </DefaultButton>
            <Tooltip title='You can enable ability to purchase your assets in 3D world for other users.'>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <span>Your products: </span>
          {products.map((el: any) => {
            return (
              <div
                className={styles.card}
                key={el.node.id}
                onClick={() => {
                  if (!el?.node?.images?.edges?.length) {
                    return;
                  }

                  loadAsset(
                    el.node.title,
                    el.node.images.edges[0].node.originalSrc,
                    el.node.id
                  );
                }}
              >
                {el?.node?.images?.edges?.length ? (
                  <img
                    src={el.node.images.edges[0].node.originalSrc}
                    width={150}
                    height={150}
                    className={styles.productImage}
                    alt='Shopify product`s shopify image'
                  />
                ) : (
                  <Empty description='' className={styles.emptyImg} />
                )}
                <div>{el.node.title}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <Empty description='' />
          <p>You don't have products in the marketplace yet.</p>
        </div>
      )}
      <Modal
        open={openModal}
        onCancel={() => setOpenModal(false)}
        width={650}
        footer={false}
      >
        <div>
          <div className={ecmrceStyles.ecmrce}>
            <h3>Assets in the scene</h3>
            <Tooltip title='You can choose which asset could be open to purchase. Only images and models are supported now.'>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div className={ecmrceStyles.assetGrid}>
            {!sceneAssets?.findManyUserSceneAssets.length && <Empty />}
            {sceneAssets?.findManyUserSceneAssets.map((el, i) => {
              return <EcommerceElementShopify key={`${el.id} ${i}`} el={el} />;
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShopifyProducts;
